import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminDashboard.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin, Company } from './AdminContext';
import { useToast } from '../../contexts/ToastContext';
import { companiesAPI } from '../../services/api';
import { AdminTable, ColumnDef, FilterDef } from './AdminTable';

export const CompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { companies, loading, refreshData } = useAdmin();

  const handleBulkDelete = useCallback(async (ids: string[]) => {
    if (window.confirm(`Weet je zeker dat je ${ids.length} klant(en) wilt verwijderen?`)) {
      try {
        await Promise.all(ids.map((id) => companiesAPI.delete(id)));
        toast.success(`${ids.length} klant(en) verwijderd`);
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  }, [refreshData, toast]);

  const columns: ColumnDef<Company>[] = [
    {
      key: 'logo',
      label: 'Logo',
      render: (company) =>
        company.logo ? (
          <img
            src={company.logo}
            alt={`${company.name} logo`}
            style={{ height: '30px', width: 'auto', objectFit: 'contain' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div style={{
            width: '30px', height: '30px', borderRadius: '6px',
            background: 'rgba(30, 58, 47, 0.1)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#1E3A2F', fontWeight: 600, fontSize: '0.75rem',
          }}>
            {company.name.charAt(0).toUpperCase()}
          </div>
        ),
    },
    {
      key: 'name',
      label: 'Naam',
      sortable: true,
      render: (company) => <span style={{ fontWeight: 600 }}>{company.name}</span>,
      exportValue: (company) => company.name,
    },
    {
      key: 'projects',
      label: 'Projecten',
      sortable: true,
      render: (company) => <span style={{ fontWeight: 600 }}>{(company as any).projectCount ?? company.projects?.length ?? 0}</span>,
      exportValue: (company) => String((company as any).projectCount ?? company.projects?.length ?? 0),
    },
    {
      key: 'users',
      label: 'Gebruikers',
      sortable: true,
      render: (company) => <span style={{ fontWeight: 600 }}>{(company as any).userCount ?? company.users?.length ?? 0}</span>,
      exportValue: (company) => String((company as any).userCount ?? company.users?.length ?? 0),
    },
    {
      key: 'subscription',
      label: 'Abonnement',
      render: (company) => (
        <span className={`${styles.statusBadge} ${styles.statusActive}`}>
          {company.subscription?.plan || 'Basic'}
        </span>
      ),
      exportValue: (company) => company.subscription?.plan || 'Basic',
    },
  ];

  const subscriptionPlans = Array.from(new Set(companies.map((c) => c.subscription?.plan || 'Basic')));

  const filters: FilterDef[] = [
    {
      key: 'plan',
      label: 'Alle abonnementen',
      options: subscriptionPlans.map((p) => ({ value: p, label: p })),
    },
  ];

  return (
    <AdminTable<Company>
      data={companies}
      columns={columns}
      filters={filters}
      filterFn={(company, activeFilters) => {
        if (activeFilters.plan && (company.subscription?.plan || 'Basic') !== activeFilters.plan) return false;
        return true;
      }}
      searchFn={(company, query) =>
        company.name.toLowerCase().includes(query) ||
        (company.contactPerson || '').toLowerCase().includes(query) ||
        (company.contactEmail || company.email || '').toLowerCase().includes(query)
      }
      onRowClick={(company) => navigate(`/admin/companies/${company._id}`)}
      getId={(company) => company._id}
      onBulkDelete={handleBulkDelete}
      exportFilename="klanten"
      loading={loading}
      emptyIcon={
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      }
      emptyTitle="Nog geen klanten"
      emptyDescription="Voeg je eerste klant toe om te beginnen."
      emptyAction={
        <Button onClick={() => navigate('/admin/companies/new')}>
          + Nieuwe klant
        </Button>
      }
      headerTitle="Klanten"
      headerAction={
        <Button onClick={() => navigate('/admin/companies/new')}>
          + Nieuwe klant
        </Button>
      }
    />
  );
};
